"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.StateMachine = void 0;
const rfc6902_1 = require("rfc6902");
class StateMachine {
  status = 'idle';
  api;
  uiController;
  eventId;
  draft;
  revision = null;
  timer;
  // default polling interval is 5 seconds
  pollingIntervalMillis = 5000;
  pendingSaveChanges = [];
  saveChangesTimeoutID;
  saveChangesDebounceMillis = 500;
  constructor(eventId, apiService, uiController, pollingIntervalMillis, saveChangesDebounceMillis) {
    this.eventId = eventId;
    this.api = apiService;
    this.uiController = uiController;
    if (pollingIntervalMillis) {
      this.pollingIntervalMillis = pollingIntervalMillis;
    }
    if (saveChangesDebounceMillis || saveChangesDebounceMillis === 0) {
      this.saveChangesDebounceMillis = saveChangesDebounceMillis;
    }
  }
  async start() {
    await this.fetchDraftAndRevision();
    this.timer = setInterval(() => this.fetchRevisions(), this.pollingIntervalMillis);
  }
  stop() {
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.revision = null;
    this.draft = {
      workflows: []
    };
  }
  getDraft() {
    return this.getDraftCopy();
  }
  async saveChanges(localDraft) {
    return new Promise((res, rej) => {
      if (this.saveChangesTimeoutID) {
        clearTimeout(this.saveChangesTimeoutID);
      }
      // create a deep copy.
      const localDrafCopy = JSON.parse(JSON.stringify(localDraft));
      this.saveChangesTimeoutID = setTimeout(() => {
        // last save call still hasn't got server response.
        if (this.status === 'saving') {
          this.saveChanges(localDrafCopy);
          return;
        }
        const currentPending = [...this.pendingSaveChanges];
        this.pendingSaveChanges.length = 0;
        this._saveChangesInternal.bind(this)(localDrafCopy).then(data => currentPending.forEach(({
          resolve
        }) => resolve(data))).catch(error => currentPending.forEach(({
          reject
        }) => reject(error)));
      }, this.saveChangesDebounceMillis);
      this.pendingSaveChanges.push({
        resolve: res,
        reject: rej
      });
    });
  }
  _saveChangesInternal = async localDraft => {
    try {
      if (!this.revision) {
        return;
      }
      this.setSaving();
      const patches = (0, rfc6902_1.createPatch)(this.draft, localDraft);
      // no changes.
      if (patches.length === 0) {
        this.setIdle();
        return;
      }
      const res = await this.api.saveDraft(this.eventId, this.revision || '', patches);
      this.revision = res.revision;
      this.draft = localDraft;
      this.setIdle();
      this.uiController.onDraft(this.getDraftCopy(), 'save');
    } catch (e) {
      if (this.api.isOutOfSyncError(e)) {
        await this.fetchDraftAndRevision('redraw');
      } else {
        this.setIdle();
        this.uiController.onDraft(this.getDraftCopy(), 'save_failed');
        this.uiController.onError(e);
      }
    }
  };
  async revertToPublished() {
    try {
      this.setReverting();
      const res = await this.api.revertToPublishedDraft(this.eventId);
      this.draft = res.draft;
      this.revision = res.revision;
      this.setIdle();
      this.uiController.onDraft(this.getDraftCopy(), 'revert_to_published');
    } catch (e) {
      this.setIdle();
      this.uiController.onError(e);
    }
  }
  async publish() {
    try {
      this.setPublishing();
      const res = await this.api.publishDraft(this.eventId);
      this.setIdle();
      this.uiController.onPublishJob(res.job_id);
    } catch (e) {
      this.setIdle();
      this.uiController.onError(e);
    }
  }
  async fetchDraftAndRevision(cause = 'start') {
    if (this.status !== 'idle' && cause !== 'redraw') {
      return;
    }
    try {
      this.setFetching();
      const [ignore, res] = await this.ignoreIf(() => this.status !== 'fetching', this.api.fetchDraftAndRevision(this.eventId));
      if (ignore) {
        return;
      }
      this.draft = res.draft;
      this.revision = res.revision;
      this.setIdle();
      this.uiController.onDraft(this.getDraftCopy(), cause);
    } catch (e) {
      this.setIdle();
      this.uiController.onError(e);
    }
  }
  async fetchRevisions() {
    if (!this.revision || this.status !== 'idle') {
      return;
    }
    try {
      this.setFetching();
      const [ignore, res] = await this.ignoreIf(() => this.status !== 'fetching', this.api.fetchNewRevisions(this.eventId, this.revision));
      if (ignore) {
        return;
      }
      if (res.revision === this.revision) {
        this.setIdle();
        return;
      }
      const draftCopy = {
        ...this.draft
      };
      const patches = res.revisions.flatMap(r => r.patches);
      const patchResults = (0, rfc6902_1.applyPatch)(draftCopy, patches);
      const badPatches = patchResults.map((result, index) => {
        return result !== null ? patches[index] : null;
      }).filter(patch => patch !== null);
      if (badPatches.length !== 0) {
        await this.fetchDraftAndRevision('redraw');
        return;
      }
      this.revision = res.revision;
      this.draft = draftCopy;
      this.setIdle();
      this.uiController.onDraft(this.getDraftCopy(), 'redraw');
    } catch (e) {
      this.setIdle();
      if (this.api.isOutOfSyncError(e)) {
        await this.fetchDraftAndRevision('redraw');
        return;
      }
      this.uiController.onError(e);
    }
  }
  setIdle() {
    this.status = 'idle';
  }
  setSaving() {
    this.status = 'saving';
  }
  setFetching() {
    this.status = 'fetching';
  }
  setReverting() {
    this.status = 'reverting';
  }
  setPublishing() {
    this.status = 'publishing';
  }
  async ignoreIf(predicate, call) {
    try {
      const res = await call;
      if (predicate()) {
        return [true, res];
      }
      return [false, res];
    } catch (e) {
      if (predicate()) {
        return [true, null];
      }
      throw e;
    }
  }
  getDraftCopy() {
    return JSON.parse(JSON.stringify(this.draft || {}));
  }
}
exports.StateMachine = StateMachine;
